<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import Loading from '@/components/common/Loading';
import { $api } from '@/services';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';
import UpdateMenu from './UpdateMenu.vue';
import DeleteMenu from './Delete.vue';
import DuplicateWeekMenu from './DuplicateWeekMenu.vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import SendMenuToParent from './SendMenuToParent.vue';
import { useStore } from 'vuex';
import DaInfinityLoading from '@/components/common/daInfinityLoading/DaInfinityLoading.vue';

const store = useStore();
const listMenu = ref([]);
const isLoading = ref(false);
const modalIsOpen = ref(false);
const type = ref('');
const sideOverShow = ref(false);
const menuId = ref(null);
const content = ref('');
const weekDetail = ref(null);
const page = ref(1);
const perPage = ref(1);
const isInfinityLoading = ref(true);
const stopLoad = ref(false);
const enabledMenu = computed(() => {
	return store.state.profile.current_org.enabled_menu;
});
const startDate = ref(null);

onMounted(() => {
	getMenu();
});

onBeforeUnmount(() => {
	isInfinityLoading.value = false;
});

function createMenu() {
	weekDetail.value = null;
	type.value = 'create';
	modalIsOpen.value = true;
}
function editMenuByWeek(week) {
	type.value = 'create';
	weekDetail.value = week;
	modalIsOpen.value = true;
}
function openDeleteModal(id) {
	type.value = 'delete';
	menuId.value = id;
	modalIsOpen.value = true;
}

async function deleteMenu() {
	try {
		await $api.menu.deleteMenu(menuId.value);
		modalIsOpen.value = false;
		await store.dispatch('setAlertMessage', {
			message: 'Xoá thực đơn thành công',
			type: 'success',
		});
		updateSuccess();
	} catch (error) {
		console.log(error);
		await store.dispatch('setAlertMessage', {
			message:
				error?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
}

async function getMenu() {
	try {
		if (!isInfinityLoading.value) return;
		isLoading.value = true;
		isInfinityLoading.value = false;
		const res = await $api.menu.listMenu(page.value, perPage.value);
		if (res.data.data.length > 0) {
			listMenu.value.push(...res.data.data);
			page.value++;
			isInfinityLoading.value = true;
		} else {
			stopLoad.value = true;
			isInfinityLoading.value = false;
		}
		isLoading.value = false;
	} catch (error) {
		isLoading.value = false;
		console.log(error);
	}
}

function getDishes(week, mealId, day) {
	const mealGroup = week.menu_details.find((m) => m.meal_id === mealId);
	if (!mealGroup) return [];
	const dayGroup = mealGroup.menu_details.find((d) => d.day_of_week === day);
	if (!dayGroup) return [];
	const detail = dayGroup.menu_details[0];
	if (!detail) return [];
	return detail.menu_item_dishes || [];
}

function formatDate(date) {
	return date.split('-').reverse().join('/');
}

function todayYmd() {
	const d = new Date();
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
}

function isWeekStartBeforeToday(startDate) {
	if (!startDate) return false;
	return startDate > todayYmd();
}

function updateSuccess() {
	listMenu.value = [];
	page.value = 1;
	perPage.value = 1;
	isInfinityLoading.value = true;
	stopLoad.value = false;
	getMenu();
	modalIsOpen.value = false;
}
function sendMenuToParent(data) {
	content.value = `Thực đơn Tuần (${formatDate(data.start_date)}
										- ${formatDate(data.end_date)})`;
	menuId.value = data.id;
	sideOverShow.value = true;
}
function duplicateWeekMenu(id, week_start) {
	type.value = 'duplicate';
	menuId.value = id;
	startDate.value = week_start;
	modalIsOpen.value = true;
}
</script>
<template>
	<div>
		<div
			v-if="sideOverShow"
			:class="sideOverShow ? 'side-over--active' : ''"
		>
			<div
				class="fixed h-screen w-screen top-0 left-0 bg-black opacity-20 z-30"
				@click="sideOverShow = false"
			></div>
			<SendMenuToParent
				@close="sideOverShow = false"
				:menuId="menuId"
				:content="content"
			/>
		</div>
		<DaModalFrame v-model:open="modalIsOpen">
			<UpdateMenu
				v-if="type === 'create'"
				@close="modalIsOpen = false"
				@success="updateSuccess"
				:weekDetail="weekDetail"
				:openModal="modalIsOpen"
			/>
			<DeleteMenu
				v-if="type === 'delete'"
				:text="`Bạn có muốn xoá thực đơn này?`"
				:textUnder="`Thực đơn này sẽ được xoá khỏi dữ liệu của cơ sở.`"
				@close="modalIsOpen = false"
				@submit="deleteMenu"
			/>
			<DuplicateWeekMenu
				v-if="type === 'duplicate'"
				@close="modalIsOpen = false"
				:menuId="menuId"
				:startDate="startDate"
				@success="updateSuccess"
			/>
		</DaModalFrame>
		<div class="__report-lesson px-12 md-down:px-4 pb-30">
			<div
				class="pt-10 pb-10 flex flex-nowrap justify-between items-center"
			>
				<div
					class="text-neutral-500 font-semibold whitespace-nowrap flex flex-nowrap items-center text-4xl"
				>
					<img src="@/assets/images/icons/menu_icon.png" alt="menu" />
					<span class="ml-2">Thực đơn bán trú</span>
				</div>
				<button
					type="button"
					class="h-12 button-p btn--ghost text-center px-6 whitespace-nowrap w-max"
					@click="createMenu"
					v-if="enabledMenu"
				>
					Thêm thực đơn
				</button>
			</div>
			<div
				v-if="isLoading && listMenu.length === 0"
				class="fixed top-1/2 left-1/2"
			>
				<Loading />
			</div>
			<div v-else>
				<div v-if="listMenu.length > 0" class="__record">
					<div
						class="__record__content mb-8"
						v-for="week in listMenu"
						:key="week.id"
					>
						<div class="flex items-center justify-between gap-6">
							<div>
								<div
									class="text-neutral-700 font-semibold flex flex-nowrap items-center gap-2 text-lg"
								>
									<img
										v-if="!week.hide"
										@click="week.hide = !week.hide"
										class="cursor-pointer"
										src="@/assets/images/icons/arrow-up.png"
										alt=""
									/>
									<img
										v-else
										@click="week.hide = !week.hide"
										class="cursor-pointer"
										src="@/assets/images/icons/arrow-down.png"
										alt=""
									/>
									<img
										v-if="week.state === 'completed'"
										class="w-5 h-5"
										src="@/assets/images/icons/green-circle.png"
										alt=""
									/>
									<img
										v-else
										class="w-5 h-5"
										src="@/assets/images/icons/lock.png"
										alt=""
									/>
									<div>
										Thực đơn Tuần ({{
											formatDate(week.start_date)
										}}
										- {{ formatDate(week.end_date) }})
									</div>
								</div>
								<div
									class="text-green-500 text-xs font-semibold"
								>
									{{
										week.state === 'completed'
											? 'HOÀN THÀNH'
											: 'BẢN NHÁP'
									}}
									| TẠO BỞI
									<span class="text-blue-600 text-sm">{{
										week?.created_by_staff?.full_name
									}}</span>
									LÚC
									{{
										$filters.formatDateTime(
											week.inserted_at
										)
									}}
								</div>
							</div>
							<div class="flex items-center gap-2">
								<div
									class="flex items-center gap-1 bg-white shadow-sm rounded-md px-5 py-3 cursor-pointer"
									@click="$router.push(`/menu/${week.id}`)"
								>
									<img
										class="w-4 h-4"
										src="@/assets/images/icons/pen.png"
										alt=""
									/>
									<div
										class="text-neutral-600 text-sm font-semibold whitespace-nowrap"
									>
										Chỉnh sửa
									</div>
								</div>
								<Menu as="div" class="ml-3 relative">
									<div>
										<MenuButton
											class="rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
										>
											<span class="sr-only"
												>Open user menu</span
											>
											<svg
												fill="none"
												height="20"
												viewBox="0 0 20 20"
												width="20"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													clip-rule="evenodd"
													d="M11.875 16.25C11.875 17.2855 11.0355 18.125 10 18.125C8.96447 18.125 8.125 17.2855 8.125 16.25C8.125 15.2145 8.96447 14.375 10 14.375C11.0355 14.375 11.875 15.2145 11.875 16.25ZM11.875 10C11.875 11.0355 11.0355 11.875 10 11.875C8.96447 11.875 8.125 11.0355 8.125 10C8.125 8.96447 8.96447 8.125 10 8.125C11.0355 8.125 11.875 8.96447 11.875 10ZM11.875 3.75C11.875 4.78553 11.0355 5.625 10 5.625C8.96447 5.625 8.125 4.78553 8.125 3.75C8.125 2.71447 8.96447 1.875 10 1.875C11.0355 1.875 11.875 2.71447 11.875 3.75Z"
													fill="#94A3B8"
													fill-rule="evenodd"
												/>
											</svg>
										</MenuButton>
									</div>
									<transition
										enter-active-class="transition ease-out duration-200"
										enter-from-class="transform opacity-0 scale-95"
										enter-to-class="transform opacity-100 scale-100"
										leave-active-class="transition ease-in duration-75"
										leave-from-class="transform opacity-100 scale-100"
										leave-to-class="transform opacity-0 scale-95"
									>
										<MenuItems
											class="origin-top-right absolute right-0 mt-2 w-60 rounded-md py-1 bg-white focus:outline-none z-10 shadow-sm border border-yellow-100"
										>
											<MenuItem
												v-slot="{ active }"
												@click="editMenuByWeek(week)"
											>
												<div
													:class="active ? '' : ''"
													class="p-2 px-4 bg-white z-10 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
												>
													<img
														class="w-4 h-4"
														src="@/assets/images/icons/pen.png"
														alt=""
													/>
													<span
														class="text-neutral-700 font-normal"
														>Chỉnh sửa chung</span
													>
												</div>
											</MenuItem>
											<MenuItem
												v-slot="{ active }"
												@click="
													duplicateWeekMenu(
														week.id,
														week.start_date
													)
												"
											>
												<div
													:class="active ? '' : ''"
													class="p-2 px-4 bg-white z-10 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
												>
													<img
														class="w-4 h-4"
														src="@/assets/images/icons/copy.png"
														alt=""
													/>
													<span
														class="text-neutral-700 font-normal"
														>Tạo bản sao</span
													>
												</div>
											</MenuItem>
											<MenuItem
												v-slot="{ active }"
												@click="sendMenuToParent(week)"
												v-if="
													week.state === 'completed'
												"
											>
												<div
													:class="active ? '' : ''"
													class="p-2 px-4 bg-white z-10 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
												>
													<img
														class="w-4 h-4"
														src="@/assets/images/icons/send.png"
														alt=""
													/>
													<span
														class="text-neutral-700 font-normal"
														>Gửi tới phụ huynh</span
													>
												</div>
											</MenuItem>
											<MenuItem
												v-slot="{ active }"
												@click="
													openDeleteModal(week.id)
												"
												v-if="isWeekStartBeforeToday(week.start_date)"
											>
												<div
													:class="active ? '' : ''"
													class="p-2 px-4 bg-white z-10 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
												>
													<img
														class="w-4 h-4"
														src="@/assets/images/icons/trash.png"
														alt=""
													/>
													<span
														class="text-neutral-700 font-normal"
														>Xoá thực đơn</span
													>
												</div>
											</MenuItem>
										</MenuItems>
									</transition>
								</Menu>
							</div>
						</div>
						<div
							id="timetable__table__box"
							class="mt-4 relative"
							v-if="!week.hide"
						>
							<div
								id="timetable__table"
								class="__record__table overflow-x-auto scroll-bar"
							>
								<div
									class="overflow-hidden sm:rounded-t-2xl w-fit"
								>
									<table class="divide-y text-neutral-200">
										<thead class="bg-neutral-100 h-16">
											<tr>
												<th
													class="px-3 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
													scope="col"
												>
													Bữa ăn
												</th>
												<th
													v-for="i in week.day_of_weeks"
													:key="i"
													class="px-3 py-3 whitespace-nowrap border-l"
													scope="col"
													:style="{
														width:
															100 /
																week
																	.day_of_weeks
																	.length +
															'%',
														minWidth: '150px',
														maxWidth: '170px',
													}"
												>
													<div
														class="flex items-center justify-between w-full font-semibold"
													>
														<div
															class="uppercase text-xs text-neutral-600 tracking-wider"
														>
															{{
																i !== 7
																	? `Thứ ${
																			i +
																			1
																	  }`
																	: 'Chủ nhật'
															}}
														</div>
													</div>
												</th>
											</tr>
										</thead>
										<tbody>
											<tr
												v-for="meal in week.meals"
												:key="meal.id"
												class="bg-white text-neutral-700 border-t"
											>
												<td
													class="p-4"
													style="vertical-align: top"
												>
													<div
														class="whitespace-nowrap text-sm text-neutral-700"
													>
														{{ meal.name }}
													</div>
												</td>
												<td
													v-for="day in week.day_of_weeks"
													:key="day"
													class="border-l p-4 align-top"
													style="min-width: 150px"
												>
													<div
														v-for="dish in getDishes(
															week,
															meal.id,
															day
														)"
														:key="dish.id"
														class="text-sm text-neutral-700"
													>
														• {{ dish.dish.name }}
													</div>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
					<DaInfinityLoading
						v-if="isInfinityLoading"
						:loaded="stopLoad"
						@infinite-load="getMenu"
						:is-loading="isLoading"
					/>
				</div>
				<div
					v-else
					class="w-full h-full mt-20 flex items-center justify-center flex-col"
				>
					<img
						src="@/assets/images/illustrations/not_found.png"
						alt=""
						height="250"
						width="250"
					/>
					<div
						class="text-neutral-500 text-lg text-center"
						style="max-width: 700px"
					>
						Trung tâm chưa có thực đơn nào.
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

